import { FormattedMessage } from "react-intl";
import { useCustomerId } from "../../hooks/useCustomerId";
import {
	AlertEntityType,
	IDataAlertEntity,
} from "../../hooks/useProductionDataAlerts";
import { ChartSerie } from "../../interfaces/api/Graph";
import { DynamicRoutes } from "../../routes";
import { formatDecimalWithOptionalDigits } from "../../util/number";
import {
	InfoBalloonWithTooltip,
	TagButtonSize,
} from "../common/InfoBalloonWithTooltip";
import { Link } from "../common/Link";

export interface ProductionDataAlertProps {
	flockId: string;
	alerts: IDataAlertEntity[] | null;
	enabledTargets?: AlertEntityType[];
}

export function ProductionDataAlerts(props: ProductionDataAlertProps) {
	return (
		<div className="control">
			<div className="tags">
				<ProductionDataAlertsContent {...props} />
			</div>
		</div>
	);
}

export function ProductionDataAlertsContent({
	alerts,
	enabledTargets,
	flockId,
}: ProductionDataAlertProps) {
	const customerId = useCustomerId();

	return alerts !== null ? (
		<>
			{filterAlerts(alerts, enabledTargets).map((alert, index) => (
				<Alert
					key={index}
					flockId={flockId}
					customerId={customerId}
					alert={alert}
				/>
			))}
		</>
	) : null;
}

function filterAlerts(
	alerts: IDataAlertEntity[],
	enabledTargets?: AlertEntityType[]
): IDataAlertEntity[] {
	return enabledTargets && enabledTargets.length > 0
		? alerts.filter((alert) =>
				enabledTargets.some((target) => target === alert.target)
		  )
		: alerts;
}

function Alert({
	alert,
	flockId,
	customerId,
}: {
	alert: IDataAlertEntity;
	flockId: string;
	customerId: string;
}) {
	const { target, type, alertLimit, factor } = alert;

	const linkTo = graphWithFilter(target, flockId, customerId);

	return (
		<Link className="tag is-warning" to={linkTo}>
			<FormattedMessage id={`ProductionDataAlert_Entity_${target}`} />{" "}
			<FormattedMessage id={`ProductionDataAlert_AlertRule_${type}`} />
			<InfoBalloonWithTooltip
				alternativeIconClass="is-small has-text-white"
				buttonSize={TagButtonSize.tiny}
				alignTooltipCenter={true}
			>
				<FormattedMessage
					id={`ProductionDataAlert_Info_${target}`}
					values={{
						limit: formatDecimalWithOptionalDigits(alertLimit, 2),
						factor:
							factor &&
							formatDecimalWithOptionalDigits(factor, 1),
					}}
				/>
			</InfoBalloonWithTooltip>
		</Link>
	);
}

function graphWithFilter(
	target: AlertEntityType,
	flockId: string,
	customerId: string
) {
	const filter = mapEntityTypeToChartSerie(target);
	return DynamicRoutes.Graph.ForFlockWithFilterAndRecommendationData.get(
		customerId,
		flockId,
		filter
	);
}

function mapEntityTypeToChartSerie(target: AlertEntityType): ChartSerie {
	switch (target) {
		case AlertEntityType.EggsPerHousedHen:
			return ChartSerie.EggsPerHousedHen;
		case AlertEntityType.AverageEggWeight:
			return ChartSerie.AverageEggWeight;
		case AlertEntityType.CumulativeEggWeight:
			return ChartSerie.CumulativeEggWeight;
		case AlertEntityType.EggLayingPercentage:
			return ChartSerie.EggPerHenPercentage;
		case AlertEntityType.AverageHenWeight:
			return ChartSerie.AverageHenWeight;
		case AlertEntityType.FodderConsumption:
			return ChartSerie.FodderConsumptionPerHen;
		case AlertEntityType.MortalityRate:
			return ChartSerie.MortalityRate;
		case AlertEntityType.WaterConsumptionPerHen:
			return ChartSerie.WaterConsumptionPerHen;
	}
}
